import backgroundBenchmark from "@images/backgroundBenchmark.png"
import errorInvitationShareCoach from "@images/error/errorInvitationShareCoach.svg"
import InvitationIcon from "@images/invitationIcon.svg"
import { ClockOutlineIcon } from "@images/svgIcons/clockOutlineIcon"
import { CheckPlaneIcon } from "@images/svgIcons/status/checkPlaneIcon"
import { CorrectIcon } from "@images/svgIcons/status/correctIcon"
import { ErrorIcon } from "@images/svgIcons/status/errorIcon"
import { useUser } from "@modules/auth/hooks/useUser"
import { useBox } from "@modules/box/useBox"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { BoxStaffActionRequest, BoxStaffStatus } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffInvitation } from "@modules/boxStaff/hooks/useBoxStaff"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { ExploreCard } from "@modules/explore/components/exploreCard/exploreCard"
import { ExploreBoxItemEntity } from "@modules/explore/entities/exploreEntity"
import { RouteNavigate } from "@modules/navigation/useBaseNavigate"
import { useBoxNavigate } from "@modules/navigation/useBoxNavigate"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LocalStorageKeys } from "@modules/storage/localStorageKeys"
import { HUButton } from "@modules/ui/components/huButton"
import { HuPermissions } from "@modules/ui/components/huPermissions"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { delay } from "@modules/utils/utils"
import { Skeleton } from "primereact/skeleton"
import { useTheme } from "styled-components"

export const BoxStaffInvitation: React.FC<{}> = ({}) => {
  const user = useUser()
  const theme = useTheme()
  const { hideSidebar } = useSidebar()
  const { localStorageService } = useServicesContext()
  const { responseBoxStaffInvitation, isPending } = useBoxStaffInvitation()
  const { value: boxId } = localStorageService.load(LocalStorageKeys.boxStaffInvitation.boxId, { value: "" })
  const { result: box } = useBox(boxId)
  const { boxService, browserProfileService } = useServicesContext()
  const boxNavigate = useBoxNavigate()
  const toast = useToast()

  const invitationStatus = user?.boxesStaffMember.find((boxStaff) => boxStaff.boxId === boxId)?.status
  const invitationRole = user?.boxesStaffMember.find((boxStaff) => boxStaff.boxId === boxId)?.role

  const requestBoxStaffInvitation = async (action: BoxStaffActionRequest) => {
    if (user?.id && boxId != "") {
      await responseBoxStaffInvitation({ boxId, action })
        .then(async () => {
          boxService.updateBoxs()
          if (action === BoxStaffActionRequest.ACCEPT) {
            toast.show({
              severity: "success",
              detail: word("box.staff.invitation.toast.success"),
            })
            hideSidebar()
            browserProfileService.setNewTheme("owner")
            await delay(100)
            boxNavigate(RouteNavigate.Root, { newBoxId: boxId })
          } else if (action === BoxStaffActionRequest.REFUSE) {
            toast.show({
              severity: "success",
              detail: word("box.staff.invitation.toast.refuse"),
            })
            hideSidebar()
          }
        })
        .catch(() => {
          toast.show({
            severity: "error",
            detail: word("box.staff.invitation.toast.error"),
          })
        })
        .finally(() => {
          localStorage.removeItem(LocalStorageKeys.boxStaffInvitation.boxId)
        })
    }
  }

  return (
    <div className="pb-3">
      {/* HEADER */}
      <div
        className="relative flex h-15rem bg-neutral-200 border-round-bottom-3xl"
        style={{ marginTop: "-85px", marginRight: "-20px", marginLeft: "-20px" }}
      >
        <div className="flex flex-column justify-content-center align-items-center w-6 h-full mt-2 ml-1">
          <div
            className="bg-contain bg-no-repeat bg-center"
            style={{ backgroundImage: `url('${InvitationIcon} ')`, width: "60px", height: "60px" }}
          ></div>
          {!box ? (
            <>
              <Skeleton width="80%" height="2rem" borderRadius="0.8rem" className="mt-1" />
              <Skeleton width="50%" height="1rem" borderRadius="0.8rem" className="mt-4" />
            </>
          ) : box ? (
            <>
              <HUText fontStyle="HM" color={theme.neutralColor900} className="mt-1">
                {box.name}
              </HUText>
              <HUText fontStyle="BS" color={theme.neutralColor700} className="mt-2">
                {word("box.staff.invitation.header.subtitle")}
              </HUText>
            </>
          ) : null}
        </div>
        <div
          className="bg-cover bg-no-repeat bg-right h-full w-6"
          style={{
            background: `url('${backgroundBenchmark}')`,
            clipPath: "polygon(43% 0, 100% 0, 100% 100%, 0% 100%)",
            WebkitClipPath: "polygon(43% 0, 100% 0, 100% 100%, 0% 100%)",
          }}
        ></div>
      </div>
      {/* CONTENT */}
      {user && box && invitationStatus && invitationRole && (
        <>
          {/* TEXT */}
          <div className="flex flex-column w-full justify-content-center align-items-center text-center	mt-5">
            <>
              {invitationStatus === BoxStaffStatus.PENDING ? (
                <HUText fontStyle="TM" color={theme.neutralColor700}>
                  {word("box.staff.invitation.header.pending", {
                    box: box.name ?? "",
                    role: invitationRole ? displayRole(invitationRole) : "",
                  })}
                </HUText>
              ) : (
                <div className="flex align-items-center gap-2">
                  <CheckPlaneIcon color={theme.mainWhite} size={24} />
                  <HUText fontStyle="TM" color={theme.neutralColor700}>
                    {word("box.staff.invitation.header.accepted")}
                  </HUText>
                </div>
              )}

              <HUText fontStyle="BM" color={theme.neutralColor700} className="mt-4">
                {invitationStatus === BoxStaffStatus.PENDING
                  ? word("box.staff.invitation.subHeader.pending", {
                      box: box.name ?? "",
                      role: invitationRole ? displayRole(invitationRole) : "",
                    })
                  : word("box.staff.invitation.subHeader.accepted")}
              </HUText>
            </>
          </div>
          {/* BOX */}
          <div className="flex flex-column mt-4">
            <HUText type="label" color={theme.neutralColor700} fontStyle="LS">
              {word("box.staff.invitation.relevantGym").toUpperCase()}
            </HUText>
            <div className="flex justify-content-center align-items-center mt-2">
              {!box ? (
                <Skeleton width="320px" height="300px" className="mt-1 rounded-33" />
              ) : (
                <ExploreCard
                  item={box as ExploreBoxItemEntity}
                  disabled
                  onViewDetailsClick={() => {
                    null
                  }}
                  isSelected={false}
                />
              )}
            </div>
          </div>
          {/* PERMISSIONS  */}
          <div className="flex flex-column mt-4">
            <HuPermissions title={word("invitationDetailsInfo.permissions").toUpperCase()} role={invitationRole} />
          </div>
          {/* BUTTONS */}
          {invitationStatus === BoxStaffStatus.PENDING && (
            <div className="flex justify-content-between flex-row w-full py-4 gap-3">
              <HUButton
                type="Right_Icon"
                size="S"
                colorType="Quaternary"
                text={word("invitationDetailsInfo.refuse")}
                icon={{ icon: () => <ErrorIcon color={theme.mainWhite} width={20} height={20} /> }}
                className="w-full"
                onClick={async () => await requestBoxStaffInvitation(BoxStaffActionRequest.REFUSE)}
                disabled={!box || isPending}
              />
              <HUButton
                type="Right_Icon"
                size="S"
                colorType="Quaternary"
                text={word("invitationDetailsInfo.answerLater")}
                icon={{ icon: () => <ClockOutlineIcon color={theme.neutralColor900} width={14} height={14} /> }}
                className="w-full"
                onClick={() => {
                  hideSidebar()
                  localStorage.removeItem(LocalStorageKeys.sharedCoachInvitation.programId)
                }}
                disabled={!box || isPending}
              />
              <HUButton
                type="Right_Icon"
                size="S"
                colorType="Primary"
                text={word("invitationDetailsInfo.accept")}
                icon={{ icon: () => <CorrectIcon color={theme.mainWhite} width={20} height={20} /> }}
                className="w-full"
                onClick={async () => await requestBoxStaffInvitation(BoxStaffActionRequest.ACCEPT)}
                disabled={!box || isPending}
              />
            </div>
          )}
        </>
      )}
      {/* LOADING */}
      {!user && !box && (
        <>
          <Skeleton width="100%" height="35rem" borderRadius="0.8rem" className="mt-1" />
        </>
      )}
      {/* ERROR */}
      {user && box && !invitationStatus && (
        <div className="flex flex-column mt-6 jutify-content-center align-items-center bg-neutral-100 rounded-33">
          <HUText fontStyle="TM" color={theme.neutralColor900} className="mt-6">
            {word("box.staff.invitation.notValid.title")}
          </HUText>
          <HUText fontStyle="BM" color={theme.neutralColor900} className="mt-3 px-4 text-center">
            {word("box.staff.invitation.notValid.subtitle")}
          </HUText>
          <div
            className="flex w-full h-12rem bg-contain bg-no-repeat bg-center my-8 "
            style={{ backgroundImage: `url(${errorInvitationShareCoach})` }}
          ></div>
        </div>
      )}
    </div>
  )
}
